import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Icono, IconBubble } from "./iconos";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const VERDE   = "#7BC74D";
const VIOLETA = "#7c3aed";
const CYAN    = "#06b6d4";

const fmt = (n, dec = 0) =>
    n == null ? "—" : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtPesos = (n) => n == null ? "—" : `US$ ${fmt(n, 2)}`;

function PctBadge({ pct }) {
    if (pct == null) return <span style={{ fontSize: "0.72rem", color: "#bbb" }}>—</span>;
    const up = pct >= 0;
    return (
        <span style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: up ? "#3a7d1e" : "#c0392b",
        }}>
            {up ? "+" : ""}{pct}%
            <span style={{ color: "#aaa", fontWeight: 400, marginLeft: 4 }}>vs período anterior</span>
        </span>
    );
}

function KpiCard({ icono, label, value, pct, color }) {
    const c = color || VERDE;
    return (
        <div className="col-6 col-md-3 mb-3">
            <div className="card h-100 border-0 shadow-sm">
                <div className="card-body py-3 px-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <IconBubble color={c} size={32}>{icono}</IconBubble>
                        <span className="text-muted" style={{ fontSize: "0.78rem", fontWeight: 500 }}>{label}</span>
                    </div>
                    <div style={{ fontSize: "1.65rem", fontWeight: 700, color: "#222", lineHeight: 1.1, marginBottom: 4 }}>
                        {value}
                    </div>
                    <PctBadge pct={pct} />
                </div>
            </div>
        </div>
    );
}

export default function ZTAudiencias() {
    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();
    const [autorizado, setAutorizado] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [datos, setDatos] = useState(null);

    useEffect(() => {
        const init = async () => {
            const token = getToken();
            if (!token) { navegacion("/"); return; }
            try {
                const r = await fetch(process.env.REACT_APP_API_ZT_AUDIENCIAS, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });
                if (r.status === 401 || r.status === 403) { navegacion("/"); return; }
                if (!r.ok) {
                    setDatos({ ageGender: [], region: [] });
                    setAutorizado(true);
                    return;
                }
                const raw = await r.json();
                setDatos({
                    ageGender: JSON.parse(raw.ageGender || "[]"),
                    region:    JSON.parse(raw.region    || "[]"),
                });
                setAutorizado(true);
            } catch {
                setDatos({ ageGender: [], region: [] });
                setAutorizado(true);
            }
            finally { setCargando(false); }
        };
        init();
    }, [navegacion]);

    if (cargando) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <div className="spinner-border" style={{ color: VERDE }} />
        </div>
    );
    if (!autorizado || !datos) return null;

    const sinDatos = (datos.ageGender || []).length === 0 && (datos.region || []).length === 0;

    // Agrupar por edad: sumar reach, impressions, spend, clicks
    const edades = {};
    let totalReach = 0, totalImp = 0, totalSpend = 0, totalClicks = 0;
    (datos.ageGender || []).forEach(row => {
        const age = row.age || "?";
        const r   = parseFloat(row.reach || 0);
        const i   = parseFloat(row.impressions || 0);
        const s   = parseFloat(row.spend || 0);
        const c   = parseFloat(row.clicks || 0);
        if (!edades[age]) edades[age] = { reach: 0, imp: 0, spend: 0, clicks: 0, male: 0, female: 0 };
        edades[age].reach  += r;
        edades[age].imp    += i;
        edades[age].spend  += s;
        edades[age].clicks += c;
        if (row.gender === "male")   edades[age].male   += r;
        if (row.gender === "female") edades[age].female += r;
        totalReach  += r;
        totalImp    += i;
        totalSpend  += s;
        totalClicks += c;
    });
    const edadKeys = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"]
        .filter(k => edades[k] !== undefined || Object.keys(edades).includes(k));
    // Si Meta usa otros formatos, fallback a las keys reales
    const allEdades = Object.keys(edades).length > 0 ? Object.keys(edades).sort() : edadKeys;

    // Estimar leads por edad (proxy: reach proporcional al total) — el back puede mejorar esto
    const totalLeadsEstim = (datos.ageGender || []).reduce((s, r) => s + parseFloat(r.actions?.[0]?.value || 0), 0) || 0;

    // Por género
    const generos = { male: 0, female: 0 };
    (datos.ageGender || []).forEach(r => {
        if (r.gender === "male")   generos.male   += parseFloat(r.reach || 0);
        if (r.gender === "female") generos.female += parseFloat(r.reach || 0);
    });
    const totalGen = generos.male + generos.female;

    // Por región — top
    const regionTotal = {};
    (datos.region || []).forEach(r => {
        const reg = r.region || "Otro";
        regionTotal[reg] = (regionTotal[reg] || 0) + parseFloat(r.reach || 0);
    });
    const regionTotalSum = Object.values(regionTotal).reduce((a, b) => a + b, 0);

    // Tabla rendimiento por edad (con barras)
    const maxReachEdad = Math.max(...allEdades.map(e => edades[e]?.reach || 0), 1);

    // KPIs
    const cplProm = totalLeadsEstim > 0 && totalSpend > 0 ? totalSpend / totalLeadsEstim : null;

    // Charts
    const alcanceData = {
        labels: allEdades,
        datasets: [{
            label: "Alcance",
            data: allEdades.map(e => edades[e]?.reach || 0),
            backgroundColor: VIOLETA,
            borderRadius: 6,
        }],
    };
    const leadsEdadData = {
        labels: allEdades,
        datasets: [{
            label: "Leads",
            data: allEdades.map(e => Math.round(((edades[e]?.reach || 0) / (totalReach || 1)) * (totalLeadsEstim || 0))),
            backgroundColor: CYAN,
            borderRadius: 6,
        }],
    };

    return (
        <div className={darkMode ? "container-dark p-3" : "p-3"}>
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: "#222" }}>Audiencias y demográficos</h4>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                        Conocé mejor a tu audiencia para tomar decisiones más inteligentes.
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "0.78rem" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: VERDE }} />
                    Actualizado hace 5 min
                </div>
            </div>

            {/* KPIs */}
            <div className="row g-2 mb-3">
                <KpiCard icono={<Icono.Users />}    label="Total de leads"    value={fmt(totalLeadsEstim, 0)} pct={null} color={VERDE} />
                <KpiCard icono={<Icono.Dollar />}   label="CPL promedio"      value={fmtPesos(cplProm)}       pct={null} color={VERDE} />
                <KpiCard icono={<Icono.Target />}   label="Alcance total"     value={fmt(totalReach, 0)}      pct={null} color={VERDE} />
                <KpiCard icono={<Icono.Eye />}      label="Impresiones"       value={fmt(totalImp, 0)}        pct={null} color={VERDE} />
            </div>

            {sinDatos ? (
                <div className="card border-0 shadow-sm">
                    <div className="card-body py-5 text-center">
                        <p className="text-muted mb-0">Sin datos de audiencias disponibles. Verificá los permisos de la cuenta publicitaria.</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Rendimiento por edad + Alcance por edad + Leads por edad */}
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-lg-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-3">Rendimiento por edad</h6>
                                    <table className="table table-sm align-middle mb-2">
                                        <thead>
                                            <tr style={{ borderBottom: "1px solid #eee" }}>
                                                <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }}>Grupo de edad</th>
                                                <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">Leads</th>
                                                <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }} className="text-end">CPL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allEdades.map(e => {
                                                const data = edades[e] || { reach: 0, spend: 0 };
                                                const leadsEst = Math.round(((data.reach || 0) / (totalReach || 1)) * (totalLeadsEstim || 0));
                                                const cplEdad  = leadsEst > 0 ? data.spend / leadsEst : null;
                                                const pct = (data.reach / maxReachEdad) * 100;
                                                const cplBad = cplEdad != null && cplProm != null && cplEdad > cplProm * 1.5;
                                                return (
                                                    <tr key={e} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                                        <td style={{ padding: "8px 0" }}>
                                                            <div style={{ fontSize: "0.78rem", fontWeight: 500, marginBottom: 4 }}>{e}</div>
                                                            <div style={{ height: 6, backgroundColor: "#f0f0f0", borderRadius: 3 }}>
                                                                <div style={{ height: "100%", width: `${pct}%`, backgroundColor: VERDE, borderRadius: 3 }} />
                                                            </div>
                                                        </td>
                                                        <td className="text-end" style={{ fontSize: "0.78rem", fontWeight: 600 }}>{leadsEst || 0}</td>
                                                        <td className="text-end" style={{ fontSize: "0.78rem", color: cplBad ? "#c0392b" : "#3a7d1e", fontWeight: 600 }}>
                                                            {fmtPesos(cplEdad)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-3">Alcance por edad</h6>
                                    <Bar data={alcanceData} options={{
                                        responsive: true,
                                        plugins: { legend: { display: false } },
                                        scales: {
                                            y: { beginAtZero: true, ticks: { font: { size: 10 } }, grid: { color: "rgba(0,0,0,0.05)" } },
                                            x: { ticks: { font: { size: 10 } }, grid: { display: false } },
                                        },
                                    }} />
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-3">Leads por edad</h6>
                                    <Bar data={leadsEdadData} options={{
                                        responsive: true,
                                        plugins: { legend: { display: false } },
                                        scales: {
                                            y: { beginAtZero: true, ticks: { font: { size: 10 }, precision: 0 }, grid: { color: "rgba(0,0,0,0.05)" } },
                                            x: { ticks: { font: { size: 10 } }, grid: { display: false } },
                                        },
                                    }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Distribución género + Geografía + Hallazgos */}
                    <div className="row g-3">
                        <div className="col-12 col-lg-5">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-3">Distribución por género</h6>
                                    <div className="row g-2">
                                        {[
                                            { key: "todos",   label: "Todos",   count: totalGen, pct: 100, color: VERDE,    icon: <Icono.Users /> },
                                            { key: "female",  label: "Mujeres", count: generos.female, pct: totalGen > 0 ? generos.female / totalGen * 100 : 0, color: "#e83e8c", icon: <Icono.Female /> },
                                            { key: "male",    label: "Hombres", count: generos.male,   pct: totalGen > 0 ? generos.male   / totalGen * 100 : 0, color: CYAN,     icon: <Icono.Male /> },
                                        ].map(g => (
                                            <div key={g.key} className="col-4">
                                                <div className="card border-0" style={{ backgroundColor: `${g.color}10` }}>
                                                    <div className="card-body p-2">
                                                        <div className="d-flex align-items-center gap-1 mb-1">
                                                            <IconBubble color={g.color} size={24}>{g.icon}</IconBubble>
                                                            <span style={{ fontSize: "0.74rem", color: "#666" }}>{g.label}</span>
                                                        </div>
                                                        <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>{fmt(g.count, 0)} <span style={{ fontSize: "0.7rem", color: "#888", fontWeight: 400 }}>leads</span></div>
                                                        <div style={{ fontSize: "0.85rem", fontWeight: 600, color: g.color, marginBottom: 4 }}>{fmt(g.pct, 1)}%</div>
                                                        <div style={{ height: 4, backgroundColor: "#fff", borderRadius: 2 }}>
                                                            <div style={{ height: "100%", width: `${g.pct}%`, backgroundColor: g.color, borderRadius: 2 }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-3">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-3">Geografía principal</h6>
                                    {Object.entries(regionTotal).sort((a, b) => b[1] - a[1]).slice(0, 1).map(([reg, val]) => {
                                        const pct = regionTotalSum > 0 ? Math.round(val / regionTotalSum * 100) : 0;
                                        return (
                                            <div key={reg} className="text-center">
                                                <Icono.MapPin color={VERDE} width={48} height={48} />
                                                <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#222", marginTop: 8 }}>{reg}</div>
                                                <div style={{ fontSize: "1.6rem", fontWeight: 700, color: VERDE }}>{pct}%</div>
                                                <div className="text-muted" style={{ fontSize: "0.74rem" }}>del total de leads</div>
                                                <div className="mt-2" style={{ height: 4, backgroundColor: "#f0f0f0", borderRadius: 2 }}>
                                                    <div style={{ height: "100%", width: `${pct}%`, backgroundColor: VERDE, borderRadius: 2 }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {Object.keys(regionTotal).length === 0 && (
                                        <div className="text-center text-muted py-4" style={{ fontSize: "0.82rem" }}>Sin datos geográficos</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-4">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-bold mb-3">Hallazgos</h6>
                                    {[
                                        { titulo: "Mejor rendimiento", detalle: "Identificá el grupo de edad con CPL más bajo y mayor volumen.", icono: <Icono.TrendingUp />, color: VERDE },
                                        { titulo: "Bajo rendimiento",  detalle: "Pausá segmentos con CPL muy alto y poco volumen.",                  icono: <Icono.TrendingDown />, color: "#c0392b" },
                                        { titulo: "Concentrar inversión", detalle: "Enfocá presupuesto en los segmentos más eficientes.",            icono: <Icono.Target />,        color: VERDE },
                                    ].map((h, i) => (
                                        <div key={i} className="d-flex gap-2 mb-3">
                                            <IconBubble color={h.color} size={32}>{h.icono}</IconBubble>
                                            <div>
                                                <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#222" }}>{h.titulo}</div>
                                                <div style={{ fontSize: "0.75rem", color: "#666", lineHeight: 1.5 }}>{h.detalle}</div>
                                            </div>
                                        </div>
                                    ))}
                                    <button className="btn btn-sm w-100 mt-2" style={{
                                        fontSize: "0.78rem",
                                        border: "1px solid #ddd",
                                        color: "#555",
                                        fontWeight: 500,
                                    }}>
                                        Ver recomendaciones →
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
